import {Component, Input, OnInit} from "@angular/core";
import {TreeNode} from "primeng/api";
import {ExtendedComponentSchema, FormioForm} from "@formio/angular";
import {
    FormChangesDelta,
    FormComponent,
    FormDataChangesColumnSettings,
    FormFieldChange,
    FormFieldChangeAction,
    FormFieldDataChange,
    FormFieldDataUpdate
} from "@features/bb-formio/components/bb-form-version-handler/bb-form-version-handler.models";
import {IHash} from "@bbwt/interfaces";
import {TreeTableCellEditor} from "primeng/treetable/treetable";

@Component({
    selector: "bb-form-version-handler",
    templateUrl: "./bb-form-version-handler.component.html",
    styleUrls: ["./bb-form-version-handler.component.scss"],
})
export class BbFormVersionHandlerComponent implements OnInit {
    protected versionChangesTree: TreeNode<FormFieldDataChange>[] = [];
    protected versionChangesColumns: FormDataChangesColumnSettings[] = [];

    @Input() formHasData: boolean = false;

    ngOnInit(): void {
        this.versionChangesColumns = [
            {
                field: "name",
                header: "Component Name"
            },
            {
                field: "action",
                header: "Action"
            },
            {
                field: "value",
                header: "Default Value",
                hide: () => !this.formHasData,
                empty: change => change.value === null,
                editable: change => change.action === FormFieldChangeAction.Add && change.editable
            }
        ];
    }

    public getFormChangesDelta(originalForm: FormioForm, updatedForm: FormioForm): FormChangesDelta {
        // Get components of original form omitting those whose key is in the list of exclusions.
        const originalComponents: FormComponent[] = this.getFormComponents(originalForm.components);

        // Get components of updated form omitting those whose key is in the list of exclusions.
        const updatedComponents: FormComponent[] = this.getFormComponents(updatedForm.components);

        return this.getComponentChangesDelta(originalComponents, updatedComponents);
    }

    private getComponentChangesDelta(originalComponents: FormComponent[], updatedComponents: FormComponent[]): FormChangesDelta {
        // Build a dictionary of key-component from original components list.
        const originalComponentsSet: IHash<FormComponent> = {};
        originalComponents.forEach(component => originalComponentsSet[component.id] = component);

        // Build a dictionary of key-component from updated components list.
        const updatedComponentsSet: IHash<FormComponent> = {};
        updatedComponents.forEach(component => updatedComponentsSet[component.id] = component);

        // Get all existing keys between original and updated components (avoiding repetitions).
        const ids: string[] = Array.from(new Set([
            ...originalComponents.map(component => component.id),
            ...updatedComponents.map(component => component.id)
        ]));

        // Detect all form changes.
        const changes: FormFieldChange[] = ids.map(id => this.getFormFieldChange(originalComponentsSet[id], updatedComponentsSet[id]));

        // Classify form changes by action type.
        const additions: FormFieldChange[] = changes
            .filter(change => change?.action === FormFieldChangeAction.Add);
        const deletions: FormFieldChange[] = changes
            .filter(change => change?.action === FormFieldChangeAction.Remove);
        const editions: FormFieldChange[] = changes
            .filter(change => change?.action === FormFieldChangeAction.Edit && this.hasFormChanges(change.changes));

        return {additions: additions, deletions: deletions, editions: editions};
    }

    private getFormFieldChange(originalComponent: FormComponent, updatedComponent: FormComponent): FormFieldChange {
        if (!originalComponent && !updatedComponent) return null;

        // Deduct form change action.
        // If both components are present, then it is an edition.
        // If only the original one is present, then it is a removal.
        // If only the updated one is present, then it is an addition.
        const action: FormFieldChangeAction = !!originalComponent && !!updatedComponent
            ? FormFieldChangeAction.Edit
            : !!originalComponent ? FormFieldChangeAction.Remove : FormFieldChangeAction.Add;

        // Set current component as the updated one
        // (except if form change action is removal, in which original one is set instead).
        const component: FormComponent = updatedComponent ?? originalComponent;

        // Get component, original and updated, inner components.
        const updatedInnerComponents: FormComponent[] = this.getInnerComponents(updatedComponent);
        const originalInnerComponents: FormComponent[] = this.getInnerComponents(originalComponent);

        // If both lists are null, then is sure this component doesn't support nesting.
        // Otherwise, make a recursive call with those components to get corresponding component changes delta.
        const changes: FormChangesDelta = !originalInnerComponents && !updatedInnerComponents
            ? null : this.getComponentChangesDelta(originalInnerComponents ?? [], updatedInnerComponents ?? []);

        return <FormFieldChange>{
            action: action,
            changes: changes,
            component: component
        }
    }

    public bindFormChangesTree(changesDelta: FormChangesDelta): void {
        this.versionChangesTree = this.bindFormChanges(changesDelta);
    }

    private bindFormChanges(delta: FormChangesDelta): TreeNode[] {
        if (!delta) return [];

        // Build each tree node corresponding to the corresponding data change.
        // Calculate its children recursively, using inner form changes delta if exists.
        const changes: FormFieldChange[] = [...delta.additions, ...delta.deletions, ...delta.editions];
        const nodes: TreeNode<FormFieldDataChange>[] = changes.map(change => <TreeNode<FormFieldDataChange>>{
            checked: true,
            expanded: true,
            data: new FormFieldDataChange(change),
            children: this.bindFormChanges(change.changes)
        });

        // Sort nodes by action, and then, by name.
        return nodes.sort((first: TreeNode<FormFieldDataChange>, second: TreeNode<FormFieldDataChange>): number =>
            this.formDataChangeComparison(first.data, second.data));
    }

    public getFormDataUpdates(): FormFieldDataUpdate[] {
        return this.getFormFieldDataUpdates(this.versionChangesTree);
    }

    private getFormFieldDataUpdates(nodes: TreeNode<FormFieldDataChange>[]): FormFieldDataUpdate[] {
        return nodes
            .filter(node => node.checked || node.partialSelected)
            .map(node => <FormFieldDataUpdate>{
                key: node.data.key,
                type: node.data.type,
                value: node.data.value,
                action: node.data.action,
                updates: this.getFormFieldDataUpdates(node.children)
            });
    }

    public applySelectedFormChanges(updatedForm: FormioForm): FormioForm {
        const form: FormioForm = JSON.parse(JSON.stringify(updatedForm));
        return this.applySelectedFormFieldChanges(form, this.versionChangesTree, true);
    }

    private applySelectedFormFieldChanges(form: FormioForm, nodes: TreeNode<FormFieldDataChange>[], updated: boolean): FormioForm {
        nodes.forEach(node => {
            switch (node.data.action) {
                case FormFieldChangeAction.Edit:
                    // If this is an edited node, do nothing and just call recursively on its children.
                    form = this.applySelectedFormFieldChanges(form, node.children, updated);
                    break;

                case FormFieldChangeAction.Add:
                    if (updated) {
                        // If this form is already updated with this addition.

                        if (node.partialSelected) {
                            // If this node is partially selected, then remove each non-selected child of this node.
                            // Do this by calling recursively on its children, indicating that the changes have
                            // already been applied (updated = true), to force the removal of unchecked ones.
                            form = this.applySelectedFormFieldChanges(form, node.children, true);

                        } else if (!node.checked) {
                            // If this node is not selected, as the addition has already been made, remove it.
                            form = this.applyFormFieldChangeAction(form, node.data, FormFieldChangeAction.Remove);
                        }
                        // If this node is selected, as the addition has already been made, do nothing.

                    } else {
                        // Otherwise, if the changes are not applied yet.

                        if (node.partialSelected) {
                            // If this node is partially selected, add it, and then remove each non-selected child of this node.
                            // Do this by calling recursively on its children, indicating that the changes have
                            // already been applied (updated = true), to force the removal of unchecked ones.
                            form = this.applyFormFieldChangeAction(form, node.data, FormFieldChangeAction.Add);
                            form = this.applySelectedFormFieldChanges(form, node.children, true);

                        } else if (node.checked) {
                            // If this node is selected, as the addition has not been made, add it.
                            form = this.applyFormFieldChangeAction(form, node.data, FormFieldChangeAction.Add);
                        }
                        // If this node is not selected, as the addition has not been made, do nothing.
                    }
                    break;

                case FormFieldChangeAction.Remove:
                    if (updated) {
                        // If this form is already updated with this removal.

                        if (node.partialSelected) {
                            // If this node is partially selected, then add it, and then remove each selected child of this node.
                            // Do this by calling recursively, indicating that the changes have not yet been
                            // applied (updated = false), to force the removal of checked children.
                            form = this.applyFormFieldChangeAction(form, node.data, FormFieldChangeAction.Add);
                            form = this.applySelectedFormFieldChanges(form, node.children, false);

                        } else if (!node.checked) {
                            // If this node is not selected, as the removal has already been made, add it.
                            form = this.applyFormFieldChangeAction(form, node.data, FormFieldChangeAction.Add);
                        }
                        // If this node is selected, as the removal has already been made, do nothing.

                    } else {
                        // Otherwise, if the changes are not applied yet.

                        if (node.partialSelected) {
                            // If this node is partially selected, then remove each selected child of this node.
                            // Do this by calling recursively, indicating that the changes have not yet been
                            // applied (updated = false), to force the removal of checked children.
                            form = this.applySelectedFormFieldChanges(form, node.children, false);

                        } else if (node.checked) {
                            // If this node is selected, as the removal has not been made, remove it.
                            form = this.applyFormFieldChangeAction(form, node.data, FormFieldChangeAction.Remove);
                        }
                        // If this node is not selected, as the removal has not been made, do nothing.
                    }
                    break;
            }
        });

        return form;
    }

    private applyFormFieldChangeAction(form: FormioForm, change: FormFieldDataChange, action: FormFieldChangeAction): FormioForm {
        if (action === FormFieldChangeAction.Edit) return form;

        const index: number = change.path.lastIndexOf(".");
        const path: string = change.path.slice(0, index);
        const key: string = change.path.slice(index + 1);

        const parent: any = this.accessNestedField(form, path);

        switch (action) {
            case FormFieldChangeAction.Add:
                if (Array.isArray(parent)) parent.splice(Number(key), 0, change.component);
                else parent[key] = change.component;
                break;
            case FormFieldChangeAction.Remove:
                if (Array.isArray(parent)) parent.splice(Number(key), 1);
                else delete parent[key];
                break;
        }

        return form;
    }

    // Auxiliary methods.
    private accessNestedField(root: any, path: string): any {
        if (root == null || path == null) return null;

        // Access the value of the given object at the given path.
        return path.split(".").reduce((obj, key) => obj?.[key] ?? null, root);
    }

    public hasFormChanges(changes: FormChangesDelta): boolean {
        if (!changes) return false;
        return !!changes.additions.length
            || !!changes.deletions.length
            || !!changes.editions.length;
    }

    private getInnerComponents(component: FormComponent): FormComponent[] {
        if (!component) return [];

        let components: FormComponent[];

        switch (component?.type) {
            case "table":
                components = component.component?.rows
                    ?.flatMap((row: ExtendedComponentSchema[], i: number) => row
                        ?.flatMap((column: ExtendedComponentSchema, j: number) =>
                            this.getFormComponents(column.components, `${component.path}.rows.${i}.${j}`)));
                break;

            case "columns":
                components = component.component?.columns
                    ?.flatMap((column: ExtendedComponentSchema, i: number) =>
                        this.getFormComponents(column.components, `${component.path}.columns.${i}`));
                break;

            default:
                components = this.getFormComponents(component.component?.components, component.path);
        }

        // Fix component indexes, by the order in which they were visited.
        components.forEach((component, i) => component.index = i);

        return components;
    }

    private getFormComponents(components: ExtendedComponentSchema[], path?: string): FormComponent[] {
        if (!components?.length) return [];

        return components?.map((component: ExtendedComponentSchema, i: number) =>
            new FormComponent(component, !path?.length ? `components.${i}` : `${path}.components.${i}`, i)) ?? [];
    }

    private formDataChangeComparison(first: FormFieldDataChange, second: FormFieldDataChange): number {
        // Sort nodes by action, and then, by name.
        if (first.action === second.action) return first.name.localeCompare(second.name);

        const actionValues: IHash<number> = {
            [FormFieldChangeAction.Add]: 0,
            [FormFieldChangeAction.Edit]: 1,
            [FormFieldChangeAction.Remove]: 2
        };

        return actionValues[first.action] - actionValues[second.action];
    }

    protected columnStyle(column: FormDataChangesColumnSettings, change: FormFieldDataChange, index: number, level: number): string {
        let style: string = "";

        // Add a tab on the left side of the cell for each level this node is deep.
        // Omit the tab if the cell is the first cell of the row (table set adds that tab automatically).
        if (index > 0) style += `margin-left: ${level * 16}px;`;

        // Gray the cell if the cell displays the default value of the data change, and it is empty.
        if (column.editable?.(change) && column.empty?.(change)) {
            style += "color: gray; cursor: pointer;";
        }

        // Set cell color if the cell displays the change action.
        if (column.field === "action") {
            switch (change.action) {
                case FormFieldChangeAction.Add:
                    style += "color: green";
                    break;
                case FormFieldChangeAction.Edit:
                    style += "color: blue";
                    break;
                case FormFieldChangeAction.Remove:
                    style += "color: red";
                    break;
            }
        }

        return style;
    }

    updateData(change: FormFieldDataChange, event: any): void {
        if (!event) return;

        if ((event.isModified || event.changed) && event.isValid) {
            change.value = event.data[change.key];
        }
    }

    clearData(change: FormFieldDataChange, editor: TreeTableCellEditor): void {
        setTimeout(() => {
            editor.editableColumn.closeEditingCell();
            change.value = null;
        }, 100);
    }
}